<template>
  <div class="p-results-check-ranks">
    <div class="p-seed-keywords-searchform">
      <div class="p-seed-keywords-searchform__left">
        <SearchForm
          :value="params.filter"
          size="sm"
          @change="updateFilter"
          @reset="resetFilter"
          placeholder="検索したいキーワードを入力"
        />
        <ButtonDownloadKeywordsV2
          :device="device"
          :from="period.value[0]"
          :project="project"
          :to="period.value[1]"
        >
          <span>CSVダウンロード</span>
        </ButtonDownloadKeywordsV2>
      </div>
      <CalendarV2
        :range="period.range"
        :value="period.value"
        @input="updatePeriod"
      />
    </div>
    <div class="p-seed-keywords-nav">
      <Pager
        v-if="hasPages"
        :limit="params.limit"
        :offset="params.offset"
        :total="filteredItems.length"
        @change="updateOffset"
      />
      <ul class="c-graph-legend">
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          1位
        </li>
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          2位～5位
        </li>
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          6位～10位
        </li>
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          11位以下
        </li>
        <li class="c-graph-legend__item c-graph-legend__item--rank">
          圏外
        </li>
        <li class="c-graph-legend__item is-nodata">
          <Icon2 variant="attention" :size="14" />データ取得中
        </li>
        <li class="c-graph-legend__item is-nodata">
          - GSC順位データなし
        </li>
        <li class="c-graph-legend__item is-nodata">
          <img
            class="c-graph-legend_icon"
            src="@/assets/img/flag-task-other.svg"
          />
          タスク完了日
        </li>
      </ul>
    </div>
    <KeywordsV2
      :items="sortedItems"
      :limitedItems="limitedItems"
      :period="period.value"
      :project="project"
      :rankItems="data.rankItems"
      :selectedId="selectedId"
      :sort="params.sort"
      :usage="usage"
      :isLoading="keywords.isLoading"
      @select="selectItem"
      @sort="updateSort"
      @displayItem="displayItem"
      @openModalAddKeyword="openModalAddKeyword"
      :listKey="listKey"
    />
    <MetricsKeywordV2
      :device="device"
      :listPeriod="period"
      :project="project"
      :selected="selected"
    />
  </div>
</template>

<script>
import { CALENDAR } from '@/constants';
import ButtonDownloadKeywordsV2 from '@/components/siteImportantKeywordGA4/ButtonDownloadKeywordsV2';
import CalendarV2 from '@/components/CalendarV2.vue';
import Icon2 from '@/components/Icon2';
import KeywordsV2 from '@/components/siteImportantKeywordGA4/KeywordsV2';
import MetricsKeywordV2 from '@/components/siteImportantKeywordGA4/MetricsKeywordV2';
import Pager from '@/components/Pager';
import SearchForm from '@/components/SearchForm';
import utils from '@/utils';
import apiProjects from '@/api/projects';
import axios from 'axios';

export default {
  props: {
    device: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: false
    },
    keyword: {
      type: String,
      required: false
    },
    keywords: {
      type: Object,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      required: false
    },
    usage: {
      type: Object,
      required: true
    }
  },
  components: {
    ButtonDownloadKeywordsV2,
    CalendarV2,
    Icon2,
    KeywordsV2,
    MetricsKeywordV2,
    Pager,
    SearchForm
  },
  data() {
    return {
      cancelTokens: {
        rankItems: {}
      },
      data: {
        items: [],
        rankItems: {}
      },
      expiresAtItems: {},
      listHeight: 0,
      params: {
        filter: '',
        offset: 0,
        limit: 100,
        sort: {
          name: 'taskDoneDate',
          orderby: 'desc',
          type: 'date'
        }
      },
      period: {
        range: [],
        value: []
      },
      request: {
        expiresAt: 10 * 60 * 1000, //10分
        timeoutDelay: 10 * 1000 //10秒
      },
      selectedId: '',
      timeoutIds: {},
      isOpenModal: this.status === 'add',
      listKey: this.generateListKeywordKey()
    };
  },
  computed: {
    dates() {
      const obj = {
        months: [],
        days: []
      };
      const startDate = new Date(this.period.value[0]);
      const endDate = new Date(this.period.value[1]);
      const total = utils.getDays(startDate, endDate);
      const d = new Date(startDate);

      for (let i = 0; i <= total; i++) {
        if (d.getDate() === 1 || i === 0) {
          let lastDate = utils.getEndDate(d, 'month');
          if (lastDate.getTime() > endDate.getTime()) {
            lastDate = new Date(endDate);
          }
          const days = utils.getDays(d, lastDate) + 1;
          const width = days * this.itemSize + 'px';
          obj.months.push({
            days: days,
            label: utils.formatDate(d, 'YYYY.MM'),
            width: width
          });
        }
        obj.days.push({
          date: new Date(d),
          label: utils.formatDate(d, 'D')
        });
        d.setDate(d.getDate() + 1);
      }
      return obj;
    },
    filteredItems() {
      if (!this.params.filter) {
        return this.data.items;
      }
      const searchString = utils.convertToSearchString(this.params.filter);

      return this.data.items.filter(item => {
        // ひらがなとカタカナを区別しないよう、ひらがなに変換
        const convertedKeyword = utils.convertToSearchString(item.keyword);
        return convertedKeyword.indexOf(searchString) !== -1;
      });
    },
    limitedItems() {
      return this.sortedItems.slice(
        this.params.offset,
        this.params.offset + this.params.limit
      );
    },
    selected() {
      return this.data.items.find(item => item.id === this.selectedId);
    },
    sortedItems() {
      //sortするためにタスク完了日パラメータの階層を変更する
      let items = this.filteredItems.slice().map(item => {
        if (item.doneTask != null) {
          item.taskDoneDate = item.doneTask.typeDoneDate;
        }
        return item;
      });

      // デフォルトでキーワード昇順のソートをかける
      items = utils.sort(items, 'string', 'asc', 'keyword');

      return utils.sort(
        items,
        this.params.sort.type,
        this.params.sort.orderby,
        this.params.sort.name
      );
    },
    hasKeyword() {
      return this.data.items.length ? true : false;
    },
    hasPages() {
      return this.filteredItems.length > this.params.limit;
    }
  },
  watch: {
    'data.items': {
      handler(value) {
        if (!this.selectedId) {
          // id（クエリパラメータ）がitemsの中に存在する場合は、そのitemを選択状態にする
          if (this.id && value.some(v => v.id === this.id)) {
            this.selectItem(this.id);
          }
        }
        this.setListHeight();
      }
    },
    device: {
      handler(value) {
        this.updateKeywordDevice(value);
      }
    },
    'keywords.items': {
      handler(value) {
        const items = value.map(item => {
          item.isLoading = true;
          if (item.id && this.data.rankItems[item.id]) {
            item.isLoading = this.data.rankItems[item.id].isLoading;
          }
          return item;
        });
        // タスクリストの導線からキーワード登録して遷移してきた場合
        if (this.id && this.status === 'new') {
          const foundItem = items.find(item => item.id === this.id);
          if (foundItem) {
            this.selectedId = this.id;
            // 登録直後なので赤色▲を表示しないようにする
            foundItem.isNew = true;
          }
        }
        this.data.items = items;
      },
      immediate: true
    }
  },
  created() {
    // 環境がdemoの場合はデモ用の表示期間で固定するようにする
    if (process.env.VUE_APP_ENV === 'demo') {
      this.period.range = utils.getSiteImportantKeywordDemoPeriod('all');
      this.period.value = utils.getSiteImportantKeywordDemoPeriod();
      this.period.range[1] = utils.getDateResetHours(this.period.range[1]);
      this.period.value[1] = utils.getDateResetHours(this.period.value[1]);
    } else {
      this.period.range = utils.getSiteImportantKeywordPeriod('all');
      this.period.value = utils.getSiteImportantKeywordPeriod();
      this.period.range[1] = utils.getDateResetHours();
      this.period.value[1] = utils.getDateResetHours();
    }
  },
  beforeDestroy() {
    this.cancelRequests();
  },
  methods: {
    getRank(id, isReRequest) {
      if (this.data.rankItems[id] && !isReRequest) {
        return;
      }
      this.data.rankItems[id] = {
        positions: {},
        isLoading: true,
        dates: [],
        dictionary: {},
        doneTasks: {}
      };
      const index = this.data.items.findIndex(item => item.id === id);
      const params = {
        from: utils.formatDate(this.period.value[0]),
        to: utils.formatDate(this.period.value[1]),
        country: 'JPN',
        device: this.device
      };
      const cancelToken = axios.CancelToken.source();
      this.cancelTokens.rankItems[id] = cancelToken;

      if (!this.expiresAtItems[id]) {
        this.expiresAtItems[id] = Date.now() + this.request.expiresAt;
      }

      return apiProjects.observationKeywords.detail
        .get(
          params,
          this.auth.idToken,
          this.project.id,
          id,
          cancelToken.token,
          index + 1
        )
        .then(response => {
          // itemsが空の場合はデータを再取得する
          if (!response.data.items.length) {
            return Promise.reject();
          }
          const item = {};
          item.dates = response.data.items.filter(d => {
            const date = utils.getDateResetHours(d.date);
            // 指定期間範囲内のデータのみitemsに入れる
            // FakeAPIの場合、期間を1日のみ選択した時でも全データを返してくるため
            // FakeじゃないAPIの場合でも、直近3日間のデータはカットする
            if (
              date >= this.period.value[0] &&
              date <= this.period.value[1] &&
              date <= CALENDAR.gsc.maxDate
            ) {
              return true;
            }
          });
          item.dictionary = response.data.dictionary;
          item.isLoading = false;
          item.doneTasks = response.data.doneTasks;
          item.positions = this.dates.days.reduce((acc, day) => {
            const key = utils.formatDate(day.date, 'YYYY-MM-DD');
            let position = null;
            // 直近3日間の順位はnull = データ取得中扱いにする
            if (day.date <= CALENDAR.gsc.maxDate) {
              if (item.dictionary) {
                if (item.dictionary[key] !== undefined) {
                  position = item.dictionary[key];
                }
              } else if (item.dates) {
                // 順位辞書がない場合はitem.datesの中で日付が同じものを探す
                const time1 = day.date.getTime();
                const result = item.dates.find(item2 => {
                  const time2 = utils.getDateResetHours(item2.date).getTime();
                  return time2 === time1;
                });
                if (result) {
                  position = result.position;
                }
              }
            }

            let type = null;
            let isProposal = null;
            if (item.doneTasks) {
              if (item.doneTasks[key] !== undefined) {
                type = item.doneTasks[key].type;
                isProposal = item.doneTasks[key].isProposal;
              }
            }

            acc[key] = {
              id,
              position,
              isLoading: false,
              type,
              isProposal
            };
            return acc;
          }, {});
          this.data.rankItems = Object.assign({}, this.data.rankItems, {
            [id]: item
          });
          this.data.items.splice(index, 1, {
            ...this.data.items[index],
            isLoading: false
          });
        })
        .catch(error => {
          // 非同期処理をキャンセルした時もここを通るが、それ以外の時
          if (!axios.isCancel(error)) {
            // 初回リクエストから10分以内であれば再取得のタイマー設定
            if (Date.now() < this.expiresAtItems[id]) {
              this.timeoutIds[id] = setTimeout(() => {
                this.getRank(id, true);
              }, this.request.timeoutDelay);
            } else {
              // 10分経っても取得できなかったら諦める
              const item = {
                positions: {},
                isLoading: false,
                dates: [],
                dictionary: {},
                doneTasks: {}
              };
              this.data.rankItems = Object.assign({}, this.data.rankItems, {
                [id]: item
              });
              this.data.items.splice(index, 1, {
                ...this.data.items[index],
                isLoading: false
              });
            }
          }
        });
    },
    cancelRequests() {
      // タイマーと非同期通信を止める
      Object.keys(this.cancelTokens.rankItems).forEach(id => {
        if (this.cancelTokens.rankItems[id]) {
          this.cancelTokens.rankItems[id].cancel();
        }
      });
      Object.keys(this.timeoutIds).forEach(id => {
        if (this.timeoutIds[id]) {
          clearTimeout(this.timeoutIds[id]);
        }
      });
    },
    selectItem(id, index) {
      this.selectedId = id;
      // 選択されたキーワードは表示されてなくても順位を取得する
      this.getRank(this.selectedId);
      // 選択したキーワードがテーブルに表示されていない場合は表示する
      if (
        index < this.params.offset ||
        index >= this.params.offset + this.params.limit
      ) {
        // indexをlimitの倍数で丸める
        const offset =
          Math.floor(index / this.params.limit) * this.params.limit;
        this.params.offset = offset;
      }
    },
    resetRanks() {
      // 全キーワードの順位の情報を初期値に戻す
      this.cancelRequests();
      this.data.items = this.data.items.map(item => {
        return { ...item, isLoading: true };
      });
      this.data.rankItems = {};
    },
    updatePeriod(value) {
      this.period.value = value;
      this.resetRanks();
      // リセット後、選択中のキーワードの順位を更新
      if (this.hasKeyword && this.selectedId != '') {
        this.getRank(this.selectedId);
      }
    },
    updateFilter(value) {
      this.params.filter = value;
      this.params.offset = 0;
      // フィルタリングをかけると無駄にスクロールが残るので再レンダリングをする
      this.listKey = this.generateListKeywordKey();

      if (process.env.VUE_APP_ENV === 'demo') {
        return;
      }
      amplitude
        .getInstance()
        .logEvent('Search Keyword - Site Important Keyword');
    },
    resetFilter() {
      this.params.filter = '';
      this.params.offset = 0;
    },
    updateOffset(offset) {
      this.params.offset = offset;
      // ページ移動したときにスクロールが残るので再レンダリングする
      this.listKey = this.generateListKeywordKey();
    },
    updateKeywordDevice(id) {
      this.device = id;
      this.resetRanks();
      // リセット後、選択中のキーワードの順位を更新
      if (this.hasKeyword && this.selectedId != '') {
        this.getRank(this.selectedId);
      }
    },
    updateSort(sort) {
      this.params.offset = 0;
      this.params.sort = sort;
    },
    addKeywords(items) {
      this.closeModal();

      items = items.map(item => {
        item.isLoading = true;
        // 登録直後なので赤色▲を表示しないようにする
        item.isNew = true;
        return item;
      });
      this.data.items = this.data.items.concat(items);
    },
    setListHeight() {
      this.$nextTick(() => {
        if (!this.$refs.listItems) {
          return;
        }
        this.listHeight = this.$refs.listItems.clientHeight;
      });
    },
    openModal() {
      this.isOpenModal = true;
    },
    closeModal() {
      this.isOpenModal = false;
    },
    displayItem(id) {
      this.getRank(id);
    },
    reset() {
      this.selectedId = '';
      if (process.env.VUE_APP_ENV === 'demo') {
        return;
      }
      amplitude
        .getInstance()
        .logEvent('Unselect Keyword - Site Important Keyword');
    },
    openModalAddKeyword() {
      this.$emit('openModalAddKeyword');
    },
    generateListKeywordKey() {
      return `listKeyword-${Date.now()}`;
    }
  }
};
</script>
